import { useState } from 'react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { StatusDot } from '../../components/ui/StatusDot';
import { BarraFiltros, type ChipFiltro } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import { EstadoSensor, type EstadoSensorValor } from '../../../domain/value-objects/EstadoSensor';
import { Dominio, type DominioValor } from '../../../domain/value-objects/Dominio';
import type { Sensor } from '../../../domain/entities/Sensor';
import { DetalleSensor } from './DetalleSensor';
import { TarjetaSensor } from './TarjetaSensor';
import { MODOS_VISTA, useMonitoreo, type ModoVista } from './useMonitoreo';
import estilos from './Monitoreo.module.css';

/** Estados que merecen un acceso rápido en la parte superior. */
const ACCESOS: EstadoSensorValor[] = ['critico', 'alerta', 'sin_datos', 'sin_umbrales'];

const OPCIONES_TIPO = [
  { valor: 'analogico', etiqueta: 'Analógicos' },
  { valor: 'digital', etiqueta: 'Digitales' },
];

export function Monitoreo() {
  const m = useMonitoreo();

  // Qué sensor tiene la ficha abierta. Se guarda la entidad y no su
  // identificador para que la ficha siga mostrando el valor al dia: la lista se
  // recarga con cada lectura y devuelve objetos nuevos.
  const [sensorAbierto, setSensorAbierto] = useState<Sensor | null>(null);

  const sensorEnFicha = sensorAbierto
    ? (m.grupos.flatMap((g) => g.sensores).find((s) => s.id === sensorAbierto.id) ?? sensorAbierto)
    : null;

  const chips: ChipFiltro[] = [];
  if (m.cascada.seleccion.clienteId) {
    const nombre = m.cascada.clientes.find((c) => c.id === m.cascada.seleccion.clienteId)?.nombre;
    chips.push({
      clave: 'cliente',
      etiqueta: `Cliente: ${nombre ?? m.cascada.seleccion.clienteId}`,
      quitar: () => m.cascada.elegirCliente(null),
    });
  }
  if (m.cascada.seleccion.areaId) {
    const nombre = m.cascada.areas.find((a) => a.id === m.cascada.seleccion.areaId)?.nombre;
    chips.push({
      clave: 'area',
      etiqueta: `Área: ${nombre ?? m.cascada.seleccion.areaId}`,
      quitar: () => m.cascada.elegirArea(null),
    });
  }
  if (m.cascada.seleccion.nodoId) {
    const nombre = m.cascada.nodos.find((n) => n.id === m.cascada.seleccion.nodoId)?.nombre;
    chips.push({
      clave: 'nodo',
      etiqueta: `Nodo: ${nombre ?? m.cascada.seleccion.nodoId}`,
      quitar: () => m.cascada.elegirNodo(null),
    });
  }
  if (m.categoria) {
    chips.push({
      clave: 'categoria',
      etiqueta: `Categoría: ${Dominio.from(m.categoria).etiqueta}`,
      quitar: () => m.setCategoria(null),
    });
  }
  if (m.tipo) {
    chips.push({
      clave: 'tipo',
      etiqueta: `Tipo: ${m.tipo === 'analogico' ? 'Analógicos' : 'Digitales'}`,
      quitar: () => m.setTipo(null),
    });
  }
  for (const e of m.estados) {
    chips.push({
      clave: `estado-${e}`,
      etiqueta: `Estado: ${EstadoSensor.from(e).etiqueta}`,
      quitar: () => m.alternarEstado(e),
    });
  }
  if (m.busqueda.trim()) {
    chips.push({
      clave: 'busqueda',
      etiqueta: `Búsqueda: «${m.busqueda.trim()}»`,
      quitar: () => m.setBusqueda(''),
    });
  }

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Monitoreo"
        descripcion="Valores de todos los sensores, actualizados en vivo."
        acciones={
          <Button
            variante="secundario"
            icono="refrescar"
            compactoEnMovil
            onClick={m.refrescar}
            cargando={m.recargando}
          >
            Actualizar
          </Button>
        }
      />

      <div className={estilos.accesos}>
        {ACCESOS.map((valor) => {
          const estado = EstadoSensor.from(valor);
          const conteo = m.conteoEstados[valor] ?? 0;
          const activo = m.estados.includes(valor);

          return (
            <button
              key={valor}
              type="button"
              className={[estilos.acceso, activo ? estilos.accesoActivo : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => m.alternarEstado(valor)}
              aria-pressed={activo}
            >
              <StatusDot color={estado.color} tamano={8} etiqueta={estado.etiqueta} />
              {estado.etiqueta}
              <span className={estilos.accesoConteo}>{conteo}</span>
            </button>
          );
        })}
      </div>

      <BarraFiltros
        cascada={m.cascada}
        chips={chips}
        acciones={
          <>
            {chips.length > 0 && (
              <Button variante="fantasma" icono="cerrar" onClick={m.limpiarFiltros}>
                Limpiar filtros
              </Button>
            )}
          </>
        }
      >
        {/*
          Cómo se agrupan los sensores.

          Era una fila de cuatro pestañas en la cabecera. Como desplegable
          ocupa lo que un filtro, se alinea con ellos —antes quedaba descolgado
          arriba— y enseña sólo la vista elegida, que es la única que importa
          hasta que se quiere cambiar.
        */}
        <Select
          etiqueta="Agrupar por"
          value={m.modo}
          onChange={(e) => m.cambiarModo(e.target.value as ModoVista)}
          opciones={MODOS_VISTA.map((v) => ({ valor: v.valor, etiqueta: v.etiqueta }))}
        />

        <div className={estilos.busqueda}>
          <Input
            etiqueta="Buscar"
            icono="buscar"
            placeholder="Sensor, nodo o área"
            value={m.busqueda}
            onChange={(e) => m.setBusqueda(e.target.value)}
          />
        </div>
        <Select
          etiqueta="Categoría"
          placeholder="Todas"
          value={m.categoria ?? ''}
          onChange={(e) => m.setCategoria((e.target.value || null) as DominioValor | null)}
          opciones={Dominio.todos().map((d) => ({ valor: d.valor, etiqueta: d.etiqueta }))}
        />
        <Select
          etiqueta="Tipo de sensor"
          placeholder="Todos"
          value={m.tipo ?? ''}
          onChange={(e) => m.setTipo((e.target.value || null) as 'analogico' | 'digital' | null)}
          opciones={OPCIONES_TIPO}
        />
      </BarraFiltros>

      {m.cargando ? (
        <ListaEsqueleto filas={6} />
      ) : m.error ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={m.refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : m.totalFiltrados === 0 ? (
        <EstadoVacio
          titulo={m.totalSensores === 0 ? 'Sin sensores' : 'Ningún sensor coincide'}
          descripcion={
            m.totalSensores === 0
              ? 'Cuando se registren nodos con sensores, sus lecturas aparecerán aquí.'
              : 'Prueba a quitar algún filtro para ver más resultados.'
          }
          acciones={
            chips.length > 0 ? (
              <Button variante="secundario" onClick={m.limpiarFiltros}>
                Limpiar filtros
              </Button>
            ) : undefined
          }
        />
      ) : (
        m.grupos.map((grupo) => {
          const presencia = grupo.nodoId !== null ? m.presencia.get(grupo.nodoId) : undefined;

          return (
            <section key={grupo.clave} className={estilos.grupo}>
              <div className={estilos.grupoCabecera}>
                <h3 className={estilos.grupoTitulo}>{grupo.titulo}</h3>
                <span className={estilos.grupoConteo}>
                  {grupo.sensores.length} {grupo.sensores.length === 1 ? 'sensor' : 'sensores'}
                </span>

                {presencia &&
                  (presencia.enLinea === null ? (
                    <Badge color="var(--text-3)">Sin latido</Badge>
                  ) : (
                    <StatusDot
                      color={presencia.enLinea ? 'var(--status-success)' : 'var(--status-danger)'}
                      encendido={presencia.enLinea}
                      texto={presencia.enLinea ? 'Conectado' : 'Desconectado'}
                      tamano={8}
                    />
                  ))}
              </div>

              <div className={estilos.rejilla}>
                {grupo.sensores.map((s) => (
                  <TarjetaSensor
                    key={s.id}
                    sensor={s}
                    mostrarNodo={m.modo !== 'nodo'}
                    nodoEnLinea={s.nodoId !== null ? m.presencia.get(s.nodoId)?.enLinea : null}
                    reciente={m.cambiados.has(s.id)}
                    onAbrir={setSensorAbierto}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      <DetalleSensor
        sensor={sensorEnFicha}
        nodoEnLinea={
          sensorEnFicha?.nodoId != null ? m.presencia.get(sensorEnFicha.nodoId)?.enLinea : null
        }
        onCerrar={() => setSensorAbierto(null)}
      />
    </div>
  );
}
